import { LayoutGraph, PortPositionsMap } from './types';

const VERTICAL_MARGIN = 100;

/**
 * Calculates Y positions for nodes using a multi-pass approach:
 *
 * PASS 1 (Right to Left - Forward Pass):
 * - Start from level 0 (roots, rightmost terminals)
 * - Position level 0 nodes by stacking order
 * - For each level moving left (1, 2, ..., maxLevel):
 *   - Position nodes based on where their OUTPUTS go (already-positioned lower levels)
 *   - This works well because graphs typically have fan-in patterns (many → few)
 *
 * Each node is positioned to minimize vertical distance to connected ports,
 * while avoiding collisions.
 */
export function calculateYPositions(
    graph: LayoutGraph,
    levels: Map<number, number>,
    stackingOrder: Map<number, number>,
    portPositions?: PortPositionsMap,
): Map<number, number> {
    const yPositions = new Map<number, number>();
    const nodeMap = new Map(graph.nodes.map(node => [node.id, node]));

    // Find max level (leftmost nodes in the graph)
    const maxLevel = Math.max(...Array.from(levels.values()));

    // Group nodes by level
    const nodesByLevel = new Map<number, number[]>();
    for (let level = 0; level <= maxLevel; level++) {
        nodesByLevel.set(level, []);
    }

    for (const [nodeId, level] of levels.entries()) {
        nodesByLevel.get(level)!.push(nodeId);
    }

    // PASS 1: Right to Left (Forward Pass)
    // Position level 0 nodes (roots, rightmost) by stacking order
    const level0Nodes = nodesByLevel.get(0) || [];
    const sortedLevel0 = [...level0Nodes].sort((a, b) => {
        const stackA = stackingOrder.get(a) ?? 0;
        const stackB = stackingOrder.get(b) ?? 0;
        return stackA - stackB;
    });

    let currentY = 0;
    for (const nodeId of sortedLevel0) {
        yPositions.set(nodeId, currentY);
        const node = nodeMap.get(nodeId)!;
        currentY += node.height + VERTICAL_MARGIN;
    }

    // Process each subsequent level from right to left (1, 2, ..., maxLevel)
    // Position based on OUTPUT connections to already-positioned lower levels
    for (let level = 1; level <= maxLevel; level++) {
        positionLevelByOutputs(level, nodesByLevel, stackingOrder, yPositions, graph, nodeMap, levels, portPositions);
    }
    return yPositions;
}

/**
 * Positions nodes in a level based on where their OUTPUTS go (to already-positioned lower levels).
 * This is used in the forward pass (right to left, level 0 → maxLevel).
 * Nodes are ordered by their ideal position based on output connections.
 */
function positionLevelByOutputs(
    currentLevel: number,
    nodesByLevel: Map<number, number[]>,
    stackingOrder: Map<number, number>,
    yPositions: Map<number, number>,
    graph: LayoutGraph,
    nodeMap: Map<number, any>,
    levels: Map<number, number>,
    portPositions?: PortPositionsMap,
) {
    const nodesInLevel = nodesByLevel.get(currentLevel) || [];

    // Calculate ideal Y position for each node based on connections
    const idealPositions = new Map<number, number>();

    for (const nodeId of nodesInLevel) {
        // Find all connected nodes that have already been positioned
        const connectedInfo = findPositionedConnectedNodesWithPorts(nodeId, graph, yPositions, portPositions);

        if (connectedInfo.length === 0) {
            // No connections to positioned nodes
            // Try to estimate position based on connections to lower levels (not yet positioned)
            const estimatedPos = estimatePositionFromLowerLevelConnections(nodeId, currentLevel, graph, levels, portPositions);

            if (estimatedPos !== null) {
                // Use estimated position based on lower-level port connections
                // This helps order nodes based on which ports they connect to
                idealPositions.set(nodeId, estimatedPos);
            } else {
                // No connections at all - use stacking order as fallback
                const stackOrder = stackingOrder.get(nodeId) ?? 0;
                idealPositions.set(nodeId, stackOrder * 200); // Arbitrary spacing
            }
        } else {
            // Calculate average Y position of connected ports
            let totalY = 0;
            for (const info of connectedInfo) {
                const connectedY = yPositions.get(info.connectedNodeId) ?? 0;
                const connectedNode = nodeMap.get(info.connectedNodeId)!;

                // Use port position if available, otherwise use node center
                const targetY = info.connectedPortY !== null ? connectedY + info.connectedPortY : connectedY + connectedNode.height / 2;
                totalY += targetY;
            }
            const avgY = totalY / connectedInfo.length;

            // Adjust for this node's port or center
            const currentNode = nodeMap.get(nodeId)!;
            let currentNodeOffset = currentNode.height / 2; // Default to center

            // Calculate average of current node's port Y positions if available
            const currentNodePortYs = connectedInfo.map(info => info.currentPortY).filter((y): y is number => y !== null);

            if (currentNodePortYs.length > 0) {
                const avgCurrentPortY = currentNodePortYs.reduce((sum, y) => sum + y, 0) / currentNodePortYs.length;
                currentNodeOffset = avgCurrentPortY;
            }

            idealPositions.set(nodeId, avgY - currentNodeOffset);
        }
    }

    // Sort nodes by their ideal position (not by stacking order!)
    // This ensures nodes are arranged based on where they want to be relative to connected nodes
    const sortedNodes = [...nodesInLevel].sort((a, b) => {
        const idealA = idealPositions.get(a) ?? 0;
        const idealB = idealPositions.get(b) ?? 0;

        // If ideal positions are very close, use lower-level port connections as tie-breaker
        if (Math.abs(idealA - idealB) < 10) {
            // Check if both nodes connect to lower levels - use port ordering
            const lowerA = estimatePositionFromLowerLevelConnections(a, currentLevel, graph, levels, portPositions);
            const lowerB = estimatePositionFromLowerLevelConnections(b, currentLevel, graph, levels, portPositions);

            if (lowerA !== null && lowerB !== null) {
                // Both connect to lower levels - sort by receiving port position
                if (Math.abs(lowerA - lowerB) > 1) {
                    return lowerA - lowerB;
                }
            }

            // Fall back to stacking order
            const stackA = stackingOrder.get(a) ?? 0;
            const stackB = stackingOrder.get(b) ?? 0;
            return stackA - stackB;
        }

        return idealA - idealB;
    });

    // Position nodes while handling collisions
    const finalPositions: { nodeId: number; y: number }[] = [];

    for (const nodeId of sortedNodes) {
        const node = nodeMap.get(nodeId)!;
        let y = idealPositions.get(nodeId)!;

        // Check for collisions with already positioned nodes
        for (const positioned of finalPositions) {
            const positionedNode = nodeMap.get(positioned.nodeId)!;
            const positionedBottom = positioned.y + positionedNode.height;
            const currentBottom = y + node.height;

            // Check if there's overlap
            if (!(currentBottom + VERTICAL_MARGIN <= positioned.y || y >= positionedBottom + VERTICAL_MARGIN)) {
                // Collision detected, nudge down
                y = positionedBottom + VERTICAL_MARGIN;
            }
        }

        finalPositions.push({ nodeId, y });
        yPositions.set(nodeId, y);
    }

    // Center nodes around their collective ideal position to prevent systematic drift
    if (sortedNodes.length > 0) {
        const idealCenter = Array.from(idealPositions.values()).reduce((sum, y) => sum + y, 0) / idealPositions.size;
        const actualCenter = sortedNodes.reduce((sum, id) => sum + yPositions.get(id)!, 0) / sortedNodes.length;
        const offset = idealCenter - actualCenter;

        // Apply centering offset to all nodes in this level
        for (const nodeId of sortedNodes) {
            yPositions.set(nodeId, yPositions.get(nodeId)! + offset);
        }
    }
}

/**
 * Information about a connected node and its port positions
 */
interface ConnectedNodeInfo {
    connectedNodeId: number;
    connectedPortY: number | null; // Y position of the port on the connected node (relative to node position)
    currentPortY: number | null; // Y position of the port on the current node (relative to node position)
}

/**
 * For nodes with no connections to already-positioned nodes, find connections to lower levels
 * and estimate a position based on port ordering. This helps order nodes on the same level
 * based on which ports they connect to on the next level.
 */
function estimatePositionFromLowerLevelConnections(
    nodeId: number,
    currentLevel: number,
    graph: LayoutGraph,
    levels: Map<number, number>,
    portPositions?: PortPositionsMap,
): number | null {
    // Find connections to nodes at lower levels (currentLevel - 1, currentLevel - 2, etc.)
    for (const conn of graph.connections) {
        let connectedNodeId: number | null = null;
        let receivingPortY: number | null = null;

        if (conn.sendingNodeId === nodeId) {
            // This node sends to another node
            connectedNodeId = conn.receivingNodeId;
            const connectedLevel = levels.get(connectedNodeId);

            // Only consider connections to lower levels (not yet positioned)
            if (connectedLevel !== undefined && connectedLevel < currentLevel) {
                // Use the RECEIVING port position (which port on the target node we connect to)
                // This determines vertical ordering: nodes connecting to upper ports should be positioned higher
                if (portPositions?.has(conn.id)) {
                    receivingPortY = portPositions.get(conn.id)!.receivingPortY;
                }

                // Return the receiving port Y position as an estimate
                // Nodes connecting to lower ports on the target get higher Y values (positioned lower)
                return receivingPortY ?? 0;
            }
        }
    }

    return null;
}

/**
 * Finds all connected nodes that have already been positioned, along with port position info.
 * Looks across ALL levels, not just adjacent ones.
 */
function findPositionedConnectedNodesWithPorts(
    nodeId: number,
    graph: LayoutGraph,
    yPositions: Map<number, number>,
    portPositions?: PortPositionsMap,
): ConnectedNodeInfo[] {
    const connected: ConnectedNodeInfo[] = [];

    for (const conn of graph.connections) {
        let candidateId: number | null = null;
        let connectedPortY: number | null = null;
        let currentPortY: number | null = null;

        if (conn.sendingNodeId === nodeId) {
            candidateId = conn.receivingNodeId;
            // Current node is sending, so we need the sending port Y on current and receiving port Y on connected
            if (portPositions?.has(conn.id)) {
                const portPos = portPositions.get(conn.id)!;
                currentPortY = portPos.sendingPortY;
                connectedPortY = portPos.receivingPortY;
            }
        } else if (conn.receivingNodeId === nodeId) {
            candidateId = conn.sendingNodeId;
            // Current node is receiving, so we need the receiving port Y on current and sending port Y on connected
            if (portPositions?.has(conn.id)) {
                const portPos = portPositions.get(conn.id)!;
                currentPortY = portPos.receivingPortY;
                connectedPortY = portPos.sendingPortY;
            }
        }

        // Only include if the node has already been positioned
        if (candidateId !== null && yPositions.has(candidateId)) {
            connected.push({
                connectedNodeId: candidateId,
                connectedPortY,
                currentPortY,
            });
        }
    }

    return connected;
}
